import { ChevronLeftIcon, FedExIcon, USPSIcon } from '@c/icons';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import RadioButton from '@ui/RadioButton';
import SafeImage from '@ui/SafeImage';
import { getProductsByIds } from '@util/firestore/products';
import { formatCurrency } from '@util/index';
import { AddressDocument } from '@util/types/firestore/address';
import { ProductDocument } from '@util/types/firestore/products';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { FC, useEffect, useState } from 'react';
import { UPS } from '@c/icons/carriers';
import { useRates } from '@util/hooks/useRates';
import { formatCartItems, getShippingType, trackEvent } from '@util/analytics';

type Props = {
  onStepComplete: (shippingOptions: any) => void;
  onPreviousClick: () => void;
  shippingAddress?: AddressDocument;
};

async function getProducts(ids: string[]) {
  const response = await getProductsByIds(ids);
  return response;
}

const Shipping: FC<Props> = ({
  onStepComplete,
  onPreviousClick,
  shippingAddress,
}) => {
  const { cart, shippingSelectionComplete, calculateTax } = useShoppingCart();
  const productIds = cart.items.map((item) => item.product_id);
  const { data: products } = useQuery({
    queryKey: ['products', ...productIds],
    queryFn: () => getProducts(productIds),
    enabled: !!cart.items.length,
    staleTime: 1000 * 60 * 5, // 5 min — don't refetch on remount
    refetchOnWindowFocus: false,
  });

  if (!shippingAddress) return null;

  function handleContinueClick() {
    if (!shippingSelectionComplete || !shippingAddress) return;
    calculateTax(cart.items, shippingAddress);

    // track event
    trackEvent('add_shipping_info', {
      currency: 'USD',
      value: cart.total,
      items: formatCartItems(cart.items),
      shipping_type: getShippingType(cart.items),
    });

    onStepComplete('options');
  }

  return (
    <div className="flex flex-col gap-16">
      <h3 className="text-[1.8rem] font-semibold">Shipping options</h3>
      <div className="flex flex-col gap-4">
        {products?.map((p) => {
          return (
            <div key={`shipping-${p.id}`}>
              <ItemShippingSelect
                chooseCheapestByDefault
                key={p.id}
                product={p}
                shipTo={shippingAddress}
                useApiRoute
              />
            </div>
          );
        })}
      </div>
      <div className="flex flex-col justify-between gap-4 font-semibold sm:flex-row">
        <Button
          leadingIcon={<ChevronLeftIcon />}
          text="Return to Information"
          type="text"
          width="extraSmall"
          onClick={onPreviousClick}
        />

        <Button
          text="Continue to Payment"
          type="secondary"
          onClick={handleContinueClick}
          disabled={!shippingSelectionComplete}
        />
      </div>
    </div>
  );
};

export default Shipping;

export function ItemShippingSelect({
  product,
  shipTo,
  hideProduct = false,
  chooseCheapestByDefault = false,
  hide = false,
  useApiRoute = true,
  showRemoveOption = true,
}: {
  product: ProductDocument;
  shipTo: AddressDocument;
  hideProduct?: boolean;
  chooseCheapestByDefault?: boolean;
  hide?: boolean;
  useApiRoute?: boolean;
  showRemoveOption?: boolean;
}) {
  const {
    allRates,
    rates,
    ratesError,
    ratesLoading,
    selectOption,
    retryRates,
    foundRate,
    showAllRates,
    setShowAllRates,
    getFlatRateShipping,
    isPopularRate,
    RATE_DISPLAY_LIMIT,
  } = useRates({
    product,
    shipTo,
    chooseCheapestByDefault,
    useApiRoute,
  });

  const { removeFromCart } = useShoppingCart();

  // Two-phase loading: show reassurance after 5 seconds
  const [extendedLoading, setExtendedLoading] = useState(false);
  useEffect(() => {
    if (!ratesLoading) {
      setExtendedLoading(false);
      return;
    }
    const timer = setTimeout(() => setExtendedLoading(true), 5000);
    return () => clearTimeout(timer);
  }, [ratesLoading]);

  if (hide) return null;
  return (
    <div className="m-1 rounded-[10px] p-6 shadow-md outline outline-1 outline-brand-lightest-gray">
      {!hideProduct && (
        <div className="flex items-center">
          <SafeImage
            alt={product.title}
            src={product.thumbnail}
            height={100}
            width={100}
          />
          <div className="ml-4">
            <h5 className="whitespace-pre-wrap text-[1.6rem] font-semibold">
              {product.title}
            </h5>
            <p className="text-[1.4rem] font-semibold">
              {formatCurrency(product.price)}
            </p>
          </div>
        </div>
      )}

      {/* options */}
      <div className="mt-8">
        {/* flat rate */}
        {product.is_flat_rate === false ? (
          // multiple options
          <div className="flex w-full flex-col gap-[1.6rem]">
            {/* loading — two-phase: reassurance message after 5 seconds */}
            {ratesLoading && (
              <div className="flex w-full flex-col gap-4">
                <span className="animate-pulse text-[1.4rem] font-semibold text-brand-dark-gray" aria-live="polite">
                  {extendedLoading
                    ? 'Still working on it \u2014 carrier rates can take a moment...'
                    : 'Loading shipping options...'}
                </span>
                <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
                <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
                <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
              </div>
            )}
            {/* shipping rate options */}
            {!!rates?.length &&
              rates.map((option) => {
                const selected =
                  foundRate?.service_code === option.service_code;
                if (showAllRates || isPopularRate(option))
                  return (
                    <div key={option.rate_id} className={`flex items-center`}>
                      <div className="flex w-[8rem] justify-center">
                        {option.carrier_code === 'fedex' ? (
                          <FedExIcon />
                        ) : option.carrier_code === 'ups' ? (
                          <UPS />
                        ) : (
                          <USPSIcon />
                        )}
                      </div>
                      <div className="flex items-center gap-5">
                        <RadioButton
                          onClick={() => selectOption(option)}
                          checked={selected}
                          label=""
                        />
                        <div>
                          <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                            {option.delivery_days} Day
                            {option.delivery_days > 1 ? 's' : ''}
                          </h5>
                          <p className="text-[1.4rem] text-brand-gray">
                            {option.service_type}
                          </p>
                        </div>
                      </div>
                      <div className="ml-auto text-[1.6rem] font-semibold">
                        {formatCurrency(option.total_amount)}
                      </div>
                    </div>
                  );
              })}
            {!!allRates?.length && allRates.length > RATE_DISPLAY_LIMIT && (
              <Button
                type="text"
                text={showAllRates ? 'Show Less' : 'Show More'}
                onClick={() => setShowAllRates(!showAllRates)}
              />
            )}
            {/* error recovery — retry or remove item */}
            {(ratesError as Error) && !ratesLoading && (
              <div className="rounded-lg bg-orange-50 border border-brand-orange/30 p-6" role="alert">
                <p className="text-[1.4rem] text-brand-dark-gray">
                  We couldn&apos;t load shipping options for this item right now.
                  This is usually a brief delay with our carrier &mdash; your
                  information is safe.
                </p>
                <div className="mt-4 flex items-center gap-4">
                  <Button
                    text="Retry"
                    type="secondary"
                    height="small"
                    width="small"
                    onClick={retryRates}
                  />
                  {showRemoveOption && (
                    <button
                      type="button"
                      className="text-[1.3rem] text-brand-gray underline hover:text-brand-dark-gray"
                      onClick={() => removeFromCart(product.id)}
                      aria-label={`Remove ${product.title} from cart`}
                    >
                      Remove item
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-8 flex items-center justify-between">
            <div>
              <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                Flat Rate Shipping
              </h5>
              <p className="text-[1.4rem] text-brand-gray">
                Shipping carrier selected by seller
              </p>
            </div>
            <div className="text-[1.6rem] font-semibold">
              {getFlatRateShipping()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
